import { useState, useRef, useCallback, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { useConnectionStore } from '@/stores/connectionStore'
import { useNavVisibilityStore } from '@/stores/navVisibilityStore'
import {
  Settings,
  HelpCircle,
  PanelLeft,
  PanelLeftClose,
  ChevronDown,
  SlidersHorizontal,
  Settings2,
  type LucideIcon,
} from 'lucide-react'
import {
  NAV_GROUPS, ADVANCED_GROUPS, DOMAIN_COLORS, ALL_ADVANCED_IDS,
  type NavItem,
} from './sidebarNav'
import { SidebarConfigDialog } from './SidebarConfigDialog'

interface SidebarDomainNavProps {
  activeView: string
  onViewChange: (view: string) => void
  toggleModeButton?: React.ReactNode
}

function NavItemButton({
  item, isActive, isExpanded, onClick,
}: {
  item: NavItem
  isActive: boolean
  isExpanded: boolean
  onClick: () => void
}) {
  const Icon = item.icon
  return (
    <li className="relative">
      {isActive && isExpanded && (
        <div aria-hidden className="absolute left-1 top-2 bottom-2 w-[2px] rounded-sm bg-accent" />
      )}
      <button
        onClick={onClick}
        title={!isExpanded ? item.label : undefined}
        className={cn(
          'w-full flex items-center gap-2.5 h-9 rounded-lg transition-colors',
          isExpanded ? 'px-3' : 'justify-center px-2',
          isActive
            ? 'bg-bg-card shadow-[0_0_0_1px_var(--color-border)] font-medium'
            : 'hover:bg-warm-200/40',
        )}
      >
        <Icon
          className="w-[18px] h-[18px] flex-shrink-0"
          style={{ color: isActive ? 'var(--color-text)' : 'var(--color-text-muted)' }}
        />
        {isExpanded && (
          <span className={cn(
            'text-sm truncate flex-1 text-left',
            isActive ? 'text-text' : 'text-text-secondary',
          )}>
            {item.label}
          </span>
        )}
      </button>
    </li>
  )
}

export function SidebarDomainNav({ activeView, onViewChange, toggleModeButton }: SidebarDomainNavProps) {
  const { isConnected, isConnecting } = useConnectionStore()
  const { hiddenItems, isPinned, itemOrder, togglePinned } = useNavVisibilityStore()
  const [isHovered, setIsHovered] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set(['sources']))
  const [configOpen, setConfigOpen] = useState(false)
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>(null)

  const isExpanded = isPinned || isHovered

  useEffect(() => {
    if (ALL_ADVANCED_IDS.includes(activeView)) setAdvancedOpen(true)
    const activeGroup = NAV_GROUPS.find(g => g.collapsible && g.items.some(i => i.id === activeView))
    if (activeGroup) {
      setCollapsedGroups(prev => { const next = new Set(prev); next.delete(activeGroup.id); return next })
    }
  }, [activeView])

  const handleMouseEnter = useCallback(() => {
    if (isPinned) return
    hoverTimeout.current = setTimeout(() => setIsHovered(true), 200)
  }, [isPinned])

  const handleMouseLeave = useCallback(() => {
    if (isPinned) return
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current)
    setIsHovered(false)
  }, [isPinned])

  function orderedVisible(groupId: string, items: NavItem[]): NavItem[] {
    const order = itemOrder[groupId]
    let result = order && order.length > 0
      ? [
          ...order.map(id => items.find(i => i.id === id)!).filter(Boolean),
          ...items.filter(i => !order.includes(i.id)),
        ]
      : items
    return result.filter(item => !hiddenItems.includes(item.id))
  }

  return (
    <>
      <aside
        className={cn(
          'h-full flex flex-col flex-shrink-0 bg-warm-50 transition-all duration-200 z-20',
          isPinned
            ? 'w-52 relative border-r border-warm-200'
            : isHovered
              ? 'w-52 absolute left-0 top-0 bottom-0 shadow-lg border-r border-warm-200'
              : 'w-14 relative'
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Top toggle — always visible in both collapsed and expanded states */}
        <div className={cn(
          'flex items-center flex-shrink-0 border-b border-warm-200 py-2',
          isExpanded ? 'px-3 justify-between' : 'justify-center px-1'
        )}>
          {isExpanded && (
            <span className="text-[10px] font-semibold uppercase tracking-widest text-text-muted select-none">
              Navigation
            </span>
          )}
          <button
            type="button"
            onClick={togglePinned}
            title={isPinned ? 'Collapse sidebar' : 'Pin sidebar open'}
            className="w-7 h-7 flex items-center justify-center rounded text-text-muted hover:bg-warm-200/60 hover:text-text transition-colors"
          >
            {isPinned
              ? <PanelLeftClose className="w-4 h-4" />
              : <PanelLeft className="w-4 h-4" />
            }
          </button>
        </div>

        <nav className="flex-1 py-3 px-1.5 overflow-y-auto">
          {NAV_GROUPS.map((group) => {
            const domainColor = group.domain ? DOMAIN_COLORS[group.domain] : undefined
            const isGroupCollapsed = group.collapsible && collapsedGroups.has(group.id) && isExpanded
            const items = orderedVisible(group.id, group.items)
            if (items.length === 0) return null
            return (
              <div key={group.id} className="mb-1">
                {isExpanded && group.label && (
                  group.collapsible ? (
                    <button
                      onClick={() => setCollapsedGroups(prev => {
                        const next = new Set(prev)
                        if (next.has(group.id)) next.delete(group.id)
                        else next.add(group.id)
                        return next
                      })}
                      className="w-full flex items-center justify-between px-3 pt-3 pb-1"
                    >
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-text-muted">
                        {group.label}
                      </span>
                      <ChevronDown className={cn('w-3 h-3 text-text-muted transition-transform', !isGroupCollapsed && 'rotate-180')} />
                    </button>
                  ) : (
                    <div className="px-3 pt-3 pb-1">
                      <span
                        className="text-[10px] font-semibold uppercase tracking-widest"
                        style={{ color: domainColor || 'var(--color-text-muted)' }}
                      >
                        {group.label}
                      </span>
                      {group.subtext && (
                        <p className="text-[10px] text-text-muted mt-0.5 normal-case tracking-normal font-normal leading-tight">
                          {group.subtext}
                        </p>
                      )}
                    </div>
                  )
                )}
                {!isGroupCollapsed && (
                  <ul className="space-y-0.5">
                    {items.map(item => (
                      <NavItemButton
                        key={item.id}
                        item={item}
                        isActive={activeView === item.id}
                        isExpanded={isExpanded}
                        onClick={() => onViewChange(item.id)}
                      />
                    ))}
                  </ul>
                )}
              </div>
            )
          })}

          <div className="mt-2">
            <button
              onClick={() => isExpanded ? setAdvancedOpen(!advancedOpen) : undefined}
              title={!isExpanded ? 'Advanced' : undefined}
              className={cn(
                'w-full flex items-center gap-2.5 h-9 rounded-lg transition-colors',
                isExpanded ? 'px-3' : 'justify-center px-2',
                ALL_ADVANCED_IDS.includes(activeView)
                  ? 'text-text font-medium'
                  : 'text-text-muted hover:bg-warm-200/40 hover:text-text'
              )}
            >
              <SlidersHorizontal className="w-[18px] h-[18px] flex-shrink-0" />
              {isExpanded && (
                <>
                  <span className="text-sm flex-1 text-left">More tools</span>
                  <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', advancedOpen && 'rotate-180')} />
                </>
              )}
            </button>
            {isExpanded && advancedOpen && (
              <div className="mt-1">
                {ADVANCED_GROUPS.map(group => {
                  const items = orderedVisible(group.id, group.items)
                  if (items.length === 0) return null
                  return (
                    <div key={group.id} className="mb-1">
                      <div className="px-4 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-widest text-text-muted">
                        {group.label}
                      </div>
                      <ul className="space-y-0.5">
                        {items.map(item => (
                          <NavItemButton
                            key={item.id}
                            item={item}
                            isActive={activeView === item.id}
                            isExpanded={isExpanded}
                            onClick={() => onViewChange(item.id)}
                          />
                        ))}
                      </ul>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="pb-3 pt-2 space-y-0.5">
            <ul className="space-y-0.5">
              {([
                { id: 'settings', label: 'Settings', icon: Settings },
                { id: 'help', label: 'Help', icon: HelpCircle },
              ] as NavItem[]).map(item => (
                <NavItemButton
                  key={item.id}
                  item={item}
                  isActive={activeView === item.id}
                  isExpanded={isExpanded}
                  onClick={() => onViewChange(item.id)}
                />
              ))}
            </ul>
            <div className={cn(
              'flex items-center pt-2 mt-1 border-t border-warm-200 gap-2',
              isExpanded ? 'px-3 justify-between' : 'flex-col justify-center'
            )}>
              <div className="flex items-center gap-2">
                <div className={cn(
                  'w-2 h-2 rounded-full',
                  isConnecting ? 'bg-warning animate-pulse' :
                  isConnected ? 'bg-success' : 'bg-text-muted'
                )} />
                {isExpanded && (
                  <span className="text-[11px] text-text-muted">
                    {isConnecting ? 'Connecting...' : isConnected ? 'Connected' : 'Offline'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {toggleModeButton}
                {isExpanded && (
                  <button
                    onClick={() => setConfigOpen(true)}
                    title="Configure navigation"
                    className="w-6 h-6 flex items-center justify-center rounded text-text-muted hover:bg-warm-200/50 hover:text-text transition-colors"
                  >
                    <Settings2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </nav>
      </aside>

      <SidebarConfigDialog open={configOpen} onClose={() => setConfigOpen(false)} />
    </>
  )
}
